import type {
  DecoratedFileCollectionFile,
  DecoratedFormSubmission,
  DecoratedFormVersion,
  UndecoratedForm,
  UndecoratedUnisonProjectFormSubmission,
} from '@witmetrics/utils';
import { asyncMap, mapToKey } from '@witmetrics/utils/arrays';
import { API } from '@/api';
import { fetchProjectFileData } from '@/api/utils/projects';
import { fetchFileCollectionData } from '@/utils/files';
import { DEFAULT_PARAMS } from './constants';

export const classes = {
  overlay:
    'flex flex-col h-full w-[92vw] max-w-[1180px] overflow-hidden bg-drawer-mesh bg-no-repeat dark:bg-drawer-mesh-dark',
  content: 'grid grid-cols-[380px_1fr] grow min-h-0 overflow-hidden',
  leftColumn:
    'flex flex-col min-h-0 overflow-hidden border-r border-solid border-black/10 dark:border-white/10',
  loading: 'h-full place-center text-taupe-2 dark:text-mauve-2',
  divider: 'mt-4 mb-0',
};

export async function fetchProjectData(projectId: number, practiceId: number) {
  const [
    unisonProject,
    tasks,
    notes,
    keypoints,
    orders,
    services,
    practiceUsers,
    keypointTypes,
    { forms, formVersions, formSubmissions, unisonProjectFormSubmissions },
  ] = await Promise.all([
    API.UnisonProjects.fetchUnisonProject(projectId),
    fetchProjectTasks(projectId),
    fetchProjectNotes(projectId),
    fetchProjectKeypoints(projectId),
    fetchProjectOrders(projectId),
    fetchProjectServices(projectId),
    fetchPracticeUsers(practiceId),
    fetchKeypointTypes(practiceId),
    fetchProjectFormSubmissions(projectId),
  ]);
  return {
    unisonProject,
    tasks,
    notes,
    keypoints,
    orders,
    services,
    practiceUsers,
    keypointTypes,
    forms,
    formVersions,
    formSubmissions,
    unisonProjectFormSubmissions,
  };
}

async function fetchProjectTasks(projectId: number) {
  return (
    await API.UnisonProjects.fetchUnisonProjectTasks(projectId, DEFAULT_PARAMS)
  ).results;
}

async function fetchProjectNotes(projectId: number) {
  return (
    await API.UnisonProjects.fetchUnisonProjectNotes(projectId, DEFAULT_PARAMS)
  ).results;
}

async function fetchProjectKeypoints(projectId: number) {
  return (
    await API.UnisonProjects.fetchUnisonProjectKeypoints(
      projectId,
      DEFAULT_PARAMS
    )
  ).results;
}

async function fetchProjectOrders(projectId: number) {
  return (
    await API.UnisonProjects.fetchUnisonProjectOrders(projectId, DEFAULT_PARAMS)
  ).results;
}

async function fetchProjectFormSubmissions(projectId: number): Promise<{
  forms: UndecoratedForm[];
  formVersions: DecoratedFormVersion[];
  formSubmissions: DecoratedFormSubmission[];
  unisonProjectFormSubmissions: UndecoratedUnisonProjectFormSubmission[];
}> {
  const unisonProjectFormSubmissions =
    await API.UnisonProjects.fetchUnisonProjectFormSubmissions(
      projectId,
      DEFAULT_PARAMS
    );
  const { forms, formVersions, formSubmissions } = await fetchFormSubmissions(
    mapToKey(unisonProjectFormSubmissions.results, 'formSubmissionId')
  );
  return {
    forms,
    formVersions,
    formSubmissions,
    unisonProjectFormSubmissions: unisonProjectFormSubmissions.results,
  };
}

async function fetchFormSubmissions(submissionIds: number[]) {
  const results = await asyncMap(submissionIds, (id) =>
    API.Forms.fetchFormSubmission(id)
  );
  let forms: UndecoratedForm[] = [];
  let formVersions: DecoratedFormVersion[] = [];
  let formSubmissions: DecoratedFormSubmission[] = [];
  results.forEach(({ form, formVersion, formSubmission }) => {
    forms.push(form);
    formVersions.push(formVersion);
    formSubmissions.push(formSubmission);
  });
  return { forms, formVersions, formSubmissions };
}

async function fetchProjectServices(projectId: number) {
  return (
    await API.UnisonProjects.fetchUnisonProjectScanServices(
      projectId,
      DEFAULT_PARAMS
    )
  ).results;
}

async function fetchPracticeUsers(practiceId: number) {
  return (
    await API.Practices.fetchPracticeUsers(practiceId, {
      filters: [],
      pagination: { page: 1, pageSize: 100 },
    })
  ).results;
}

async function fetchKeypointTypes(practiceId: number) {
  return (
    await API.Practices.fetchPracticeKeypointTypes(practiceId, {
      pagination: { page: 1, pageSize: 250 }, // TODO: Just doing a giant page size to fetch everything
    })
  ).results;
}

export async function fetchProjectFiles(projectId: number) {
  // Fetch data connected to the project
  let { fileCollectionIds, projectFileCollections, fileCollectionFiles } =
    await fetchProjectFileData(projectId);
  // Fetch data connected to the file collections
  let { files, fileCollections } =
    await fetchFileCollectionData(fileCollectionIds);
  const fileNotes = await fetchFileNotes(files);
  return {
    projectFileCollections,
    fileCollectionFiles,
    fileNotes: fileNotes.map((note) => note.results).flat(),
    fileCollections,
  };
}

export function fetchFileNotes(files: DecoratedFileCollectionFile[]) {
  const params = { pagination: { page: 1, pageSize: 100 } }; // TODO: Pagination could be improved
  return asyncMap(files, (f) => API.Files.fetchFileNotes(f.fileId, params));
}
